import { useEffect, useRef } from 'react'
import { useAuth } from '../auth/useAuth'
import { audioSystem } from '../pc/feel/audio'
import { useArenaStore } from '../store'

const KEYS = [
  { key: 'W A S D', label: 'Move' },
  { key: 'Mouse', label: 'Look' },
  { key: 'E', label: 'Interact' },
  { key: 'Esc', label: 'Pause' },
]

export function ClickToPlay() {
  const isPlaying          = useArenaStore((s) => s.isPlaying)
  const setIsPlaying       = useArenaStore((s) => s.setIsPlaying)
  const isPaused           = useArenaStore((s) => s.isPaused)
  const setIsPaused        = useArenaStore((s) => s.setIsPaused)
  const isAgentTalkOpen    = useArenaStore((s) => s.isAgentTalkOpen)
  const isWorkplacePhoneOpen = useArenaStore((s) => s.isWorkplacePhoneOpen)
  const activeInteraction  = useArenaStore((s) => s.activeInteraction)
  const inspectorRunId     = useArenaStore((s) => s.inspectorRunId)
  const progress           = useArenaStore((s) => s.assetProgress)
  // In mock/dev without a canvas boot yet, total is 0 — don't block entry.
  const coreReady = progress.coreReady || progress.total === 0
  const { status, user, login } = useAuth()

  // Track previous pointer-lock state to know when we transitioned out of play
  const wasPlayingRef = useRef(false)
  const activeInteractionRef = useRef(activeInteraction)
  useEffect(() => { activeInteractionRef.current = activeInteraction }, [activeInteraction])

  useEffect(() => {
    const handler = () => {
      const locked = document.pointerLockElement !== null
      setIsPlaying(locked)
      if (!locked && wasPlayingRef.current && !activeInteractionRef.current
          && useArenaStore.getState().inspectorRunId === null
          && !useArenaStore.getState().isAgentTalkOpen
          && !useArenaStore.getState().isWorkplacePhoneOpen) {
        setIsPaused(true)
      }
      wasPlayingRef.current = locked
    }
    document.addEventListener('pointerlockchange', handler)
    return () => document.removeEventListener('pointerlockchange', handler)
  }, [setIsPlaying, setIsPaused])

  // Don't render title screen when playing, paused (pause menu handles that),
  // dialog open, or the run inspector panel is open
  if (
    isPlaying || isPaused || isAgentTalkOpen || isWorkplacePhoneOpen ||
    activeInteraction !== null || inspectorRunId !== null
  ) return null

  function enter() {
    setIsPaused(false)
    // Init AudioContext synchronously within this trusted click gesture so the
    // browser's autoplay policy allows it; PcCanvas's subscriber will just resume.
    audioSystem.init()
    document.querySelector('canvas')?.requestPointerLock()
  }

  const isAuthed = status === 'authenticated'

  return (
    <div style={s.overlay}>
      {/* Vignette */}
      <div style={s.vignette} aria-hidden="true" />

      {/* Card */}
      <div style={s.card}>
        {/* Wordmark */}
        <div style={s.wordmark}>ANIMUS ARENA</div>
        <div style={s.subtitle}>your fleet, live</div>

        {/* Enter button — doubles as the asset loader until the core batch is in */}
        <button
          style={{ ...s.enterBtn, ...(coreReady ? {} : { opacity: 0.65, cursor: 'progress' }) }}
          onClick={enter}
          disabled={!coreReady}
        >
          {coreReady
            ? <>▶ &nbsp;ENTER THE OFFICE</>
            : <>SETTING UP THE OFFICE… {progress.total > 0 ? `${progress.loaded}/${progress.total}` : ''}</>}
        </button>
        {/* Loading bar: fills through the core batch, slims out while the rest streams in */}
        {!progress.done && (
          <div style={{ width: '100%', height: 4, borderRadius: 2, background: 'rgba(245,235,224,0.15)', margin: '0.5rem 0 0' }}>
            <div
              style={{
                width: `${progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0}%`,
                height: '100%',
                borderRadius: 2,
                background: coreReady ? 'rgba(163,177,138,0.6)' : '#e07a5f',
                transition: 'width 200ms ease',
              }}
            />
          </div>
        )}

        {/* Controls card */}
        <div style={s.controlsCard}>
          {KEYS.map(({ key, label }) => (
            <div key={key} style={s.controlRow}>
              <span style={s.keycap}>{key}</span>
              <span style={s.controlLabel}>{label}</span>
            </div>
          ))}
        </div>

        {/* Footer / identity */}
        <div style={s.footer}>
          {isAuthed && user ? (
            <span style={s.identity}>
              <span style={s.identityDot}>●</span>
              {user.name ?? user.email ?? user.sub}
            </span>
          ) : (
            <button style={s.signInBtn} onClick={() => void login()}>
              Sign in
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
  },
  vignette: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.75) 100%)',
    backdropFilter: 'blur(3px)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.25rem',
    padding: '2.5rem 3rem',
    background: 'rgba(61,64,91,0.82)',
    border: '1px solid rgba(245,235,224,0.15)',
    borderRadius: '1.25rem',
    boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
    backdropFilter: 'blur(12px)',
    minWidth: '22rem',
    maxWidth: '34rem',
    width: '90vw',
  },
  wordmark: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: 'clamp(2rem, 6vw, 3.25rem)',
    fontWeight: 900,
    letterSpacing: '0.08em',
    background: 'linear-gradient(135deg, #f5ebe0 0%, #e07a5f 55%, #b08968 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.9rem',
    fontWeight: 400,
    color: 'rgba(245,235,224,0.6)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginTop: '-0.5rem',
  },
  enterBtn: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    padding: '0.75rem 2.25rem',
    borderRadius: '9999px',
    border: 'none',
    background: 'linear-gradient(135deg, #e07a5f, #b08968)',
    color: '#f5ebe0',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(224,122,95,0.4)',
    transition: 'transform 0.12s, box-shadow 0.12s',
    marginTop: '0.25rem',
  },
  controlsCard: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '0.4rem 0.875rem',
    padding: '0.875rem 1.25rem',
    background: 'rgba(0,0,0,0.25)',
    borderRadius: '0.75rem',
    border: '1px solid rgba(245,235,224,0.08)',
    width: '100%',
    alignItems: 'center',
  },
  controlRow: {
    display: 'contents',
  },
  keycap: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.72rem',
    fontWeight: 700,
    padding: '0.2rem 0.5rem',
    background: 'rgba(245,235,224,0.1)',
    border: '1px solid rgba(245,235,224,0.2)',
    borderBottom: '2px solid rgba(245,235,224,0.12)',
    borderRadius: '0.3rem',
    color: '#f5ebe0',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    letterSpacing: '0.03em',
  },
  controlLabel: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.8rem',
    color: 'rgba(245,235,224,0.55)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '0.25rem',
    borderTop: '1px solid rgba(245,235,224,0.08)',
    width: '100%',
  },
  identity: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.8rem',
    color: 'rgba(245,235,224,0.5)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  },
  identityDot: {
    color: '#a3b18a',
    fontSize: '0.6rem',
  },
  signInBtn: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.8rem',
    fontWeight: 700,
    padding: '0.3rem 0.9rem',
    border: '1px solid rgba(245,235,224,0.25)',
    borderRadius: '9999px',
    background: 'transparent',
    color: 'rgba(245,235,224,0.7)',
    cursor: 'pointer',
  },
}
